import _ from 'lodash';
import { MenuHelper } from 'src/app/helpers';

import { Menu, MenuItem, PriceType } from 'src/app/models';
import {
  ModifierPrompt,
  ModifierPromptOption,
  ModifierPromptOptionType,
  PosQuantityRestriction
} from 'src/app/models/pos-manager.model';

export class MenuStateHelper {
  static formatMenuWithPrompt(menu: Menu): MenuItem[] {
    const fullMenu: MenuItem[] = [].concat(
        menu.menu_items,
        menu.products,
        menu.also_available
      ),
      // menuItems is a clone of fullMenu and it should be use as lookup with the real menu items
      // because real menu items may contains less data due to the options such as (show_standard_menu_items, show_non_food_products, ...)
      menuItems = _.cloneDeep(fullMenu);

    // split_modifier_prompts (DEF-14217 follow-up): the request now asks the backend to
    // dedupe a modifier_prompt shared across assignments into this top-level array,
    // keyed by id, instead of repeating its question_label/options on every assignment.
    const modifierPromptsById: _.Dictionary<ModifierPrompt> = _.keyBy(
      menu.modifier_prompts || [],
      'id'
    );

    menuItems.forEach((menuItem) => {
      if (_.isEmpty(menuItem.modifier_prompt_assignments)) {
        return;
      }

      menuItem.modifier_prompt_assignments.forEach((modifierPrompt) => {
        let formattedOptions: ModifierPromptOption[] = [];
        // resolve the deduped question_label/options this assignment maps to via
        // modifier_prompt_id; fall back to whatever's already embedded so this still
        // works against a response that didn't ask for split_modifier_prompts.
        const sharedPrompt =
          modifierPromptsById[modifierPrompt.modifier_prompt_id];
        modifierPrompt.question_label =
          modifierPrompt.question_label ?? sharedPrompt?.question_label;
        const modifierPromptOptions =
          modifierPrompt.modifier_prompt_options ??
          sharedPrompt?.modifier_prompt_options;

        if (_.isNil(modifierPromptOptions)) {
          modifierPrompt.formattedOptions = formattedOptions;
          return;
        }

        modifierPromptOptions.forEach((opt) => {
          switch (opt.option_type) {
            case ModifierPromptOptionType.Category:
              // DEF-14217: the backend now merges a category-type option's member
              // items into menu.menu_items/products/also_available (even when off
              // grid), so fullMenu already carries them — no separate lookup needed.
              const optionsMenuItems = fullMenu.filter(
                (i) =>
                  i.category_id === opt.category.id &&
                  i.price_type === PriceType.FIXED
              );
              if (optionsMenuItems?.length) {
                const promptsFromMenuItem = _.sortBy(
                  optionsMenuItems,
                  'sort'
                ).map(
                  (fullMenuItem) =>
                    <ModifierPromptOption>{
                      fullMenuItem,
                      id: fullMenuItem.id,
                      text_label: fullMenuItem.name,
                      option_type: ModifierPromptOptionType.Category,
                      added_price: fullMenuItem.price?.toString(),
                      category: opt.category,
                      sort_order: opt.sort_order,
                      modifier_color: opt.modifier_color
                    }
                );
                formattedOptions = formattedOptions.concat(promptsFromMenuItem);
              }
              break;
            case ModifierPromptOptionType.PosProduct:
              const product = fullMenu.find(
                (i) => i.id === opt.pos_product.id
              ) as MenuItem;
              formattedOptions.push(
                product
                  ? {
                      ...opt,
                      text_label: product.name,
                      added_price: product.price
                        ? product.price.toString()
                        : undefined,
                      modifier_color: opt.modifier_color,

                      fullMenuItem: product
                    }
                  : opt
              );
              break;
            default:
              formattedOptions.push(opt);
              break;
          }
        });

        modifierPrompt.formattedOptions = formattedOptions;
      });
    });
    return menuItems;
  }

  static addQuantityRestrictionsToPrompts(
    menuItem: MenuItem,
    qtyRestrictions: PosQuantityRestriction[]
  ) {
    if (
      _.isEmpty(menuItem.modifier_prompt_assignments) ||
      _.isEmpty(qtyRestrictions)
    ) {
      return menuItem;
    }
    const clonedMenuItem = _.cloneDeep(menuItem);
    clonedMenuItem.modifier_prompt_assignments.forEach((modifierPrompt) => {
      if (_.isNil(modifierPrompt.formattedOptions)) {
        return;
      }

      modifierPrompt.formattedOptions?.forEach((opt) => {
        opt.soldOut = MenuHelper.isMenuItemSoldOut(
          opt.fullMenuItem?.id,
          opt.fullMenuItem?.food_id,
          opt.fullMenuItem?.type,
          qtyRestrictions
        );
      });
    });
    return clonedMenuItem;
  }

  static getBarcodeProductsDictionary(menu: Menu): _.Dictionary<MenuItem[]> {
    // Check if the menuItem is a modifier type
    const products = _.cloneDeep(
      menu.products?.filter((x) => !x.modifier) || []
    );
    const dictionary = _.groupBy(products, 'barcode');
    return dictionary;
  }
}
